import OrderDialog from "@/components/OrderDialog";
import { useState } from "react";
import { mediaUrl } from "@/lib/api";

const ACCENTS = {
  cyan: { badge: "bg-[#00F0FF]/20 text-[#00F0FF]", hover: "hover:border-[#00F0FF]/50" },
  magenta: { badge: "bg-[#FF1E8C]/20 text-[#FF1E8C]", hover: "hover:border-[#FF1E8C]/50" },
  red: { badge: "bg-[#FF2E2E]/20 text-[#FF2E2E]", hover: "hover:border-[#FF2E2E]/50" },
};

export const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const visualClass = "absolute inset-0 w-full h-full object-cover opacity-90 hover:scale-105 transition-transform duration-500";

/** Generated autoplay product loop when available, otherwise the still image. */
export function ProductVisual({ product }) {
  if (product.video_url) {
    return (
      <video
        className={visualClass}
        poster={mediaUrl(product.image_url)}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        data-testid={`product-video-${product.id}`}
      >
        <source src={mediaUrl(product.video_url)} type="video/mp4" />
        {product.video_webm && <source src={mediaUrl(product.video_webm)} type="video/webm" />}
      </video>
    );
  }
  return <img src={mediaUrl(product.image_url)} alt={product.name} loading="lazy" className={visualClass} />;
}

export function ProductCard({ product, onOrder }) {
  const accent = ACCENTS[product.accent] || ACCENTS.cyan;
  return (
    <div
      className={`border border-white/10 rounded-xl p-5 bg-[#061536]/90 ${accent.hover} transition-colors flex flex-col justify-between`}
      data-testid={`product-card-${product.id}`}
    >
      <div className="aspect-square rounded-lg mb-4 overflow-hidden relative bg-[#02050F]">
        <ProductVisual product={product} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#061536]/70 via-transparent to-transparent" />
        {product.badge && (
          <span className={`absolute top-2 right-2 font-mono-lbl text-[10px] ${accent.badge} px-2 py-0.5 rounded`}>
            {product.badge}
          </span>
        )}
      </div>
      <div>
        <h4 className="font-disp text-xl uppercase tracking-wide">{product.name}</h4>
        <p className="text-white/60 text-xs mb-3">{product.description}</p>
        <div className="flex items-center justify-between pt-3 border-t border-white/10">
          <span className="font-mono-lbl text-sm font-bold text-white" data-testid={`product-price-${product.id}`}>
            {inr(product.price_inr)}
          </span>
          <button
            type="button"
            onClick={() => onOrder(product)}
            className="bg-[#FF2E2E] hover:bg-[#E01F1F] text-white px-3 py-1.5 rounded font-mono-lbl text-[11px] uppercase font-bold tracking-wider transition-colors"
            data-testid={`product-order-btn-${product.id}`}
          >
            Order Now
          </button>
        </div>
      </div>
    </div>
  );
}

export function ProductGrid({ products, loading }) {
  const [selected, setSelected] = useState(null);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="product-grid-loading">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="border border-white/10 rounded-xl p-5 bg-[#061536]/90 animate-pulse h-80" />
        ))}
      </div>
    );
  }

  if (!products.length) {
    return (
      <div className="border border-white/10 rounded-xl p-10 bg-[#061536]/60 text-center" data-testid="product-grid-empty">
        <p className="text-white/60 text-sm">No devices match this filter yet. New AIoT hardware is added regularly.</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="product-grid">
        {products.map((p) => (
          <ProductCard key={p.id} product={p} onOrder={setSelected} />
        ))}
      </div>
      <OrderDialog product={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} />
    </>
  );
}
